<template>
  <div>
    <!-- FAB -->
    <button class="cb-fab" :class="{ 'cb-fab--open': open }" @click="toggle" title="Assistant IA">
      <span class="cb-fab-inner">
        <i v-if="!open" class="bi bi-stars"></i>
        <i v-else class="bi bi-chevron-down"></i>
      </span>
      <span v-if="!open" class="cb-fab-label">IA</span>
    </button>

    <!-- Panel -->
    <transition name="cb-slide">
      <div v-if="open" class="cb-panel">

        <!-- Header -->
        <div class="cb-header">
          <div class="cb-header-left">
            <div class="cb-avatar">
              <i class="bi bi-stars"></i>
            </div>
            <div>
              <div class="cb-header-title">Assistant IA</div>
              <div class="cb-header-sub">
                <span class="cb-dot"></span> En ligne
              </div>
            </div>
          </div>
          <div class="cb-header-actions">
            <button class="cb-icon-btn" @click="clearChat" title="Nouvelle conversation">
              <i class="bi bi-trash3"></i>
            </button>
            <button class="cb-icon-btn" @click="toggle" title="Fermer">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div class="cb-messages" ref="msgContainer">

          <!-- Welcome state -->
          <div v-if="messages.length === 0" class="cb-welcome">
            <div class="cb-welcome-icon">
              <i class="bi bi-stars"></i>
            </div>
            <p class="cb-welcome-title">Comment puis-je vous aider ?</p>
            <p class="cb-welcome-sub">Posez une question sur vos ventes, stock, clients, paiements…</p>
            <div class="cb-suggestions">
              <button
                v-for="s in suggestions"
                :key="s"
                class="cb-suggestion"
                @click="useSuggestion(s)"
              >{{ s }}</button>
            </div>
          </div>

          <!-- Message list -->
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            class="cb-msg"
            :class="msg.role === 'user' ? 'cb-msg--user' : 'cb-msg--bot'"
          >
            <div v-if="msg.role === 'bot'" class="cb-msg-avatar">
              <i class="bi bi-stars"></i>
            </div>
            <div class="cb-bubble" :class="msg.role === 'user' ? 'cb-bubble--user' : 'cb-bubble--bot'">
              <span v-if="msg.loading" class="cb-typing">
                <span></span><span></span><span></span>
              </span>
              <span v-else style="white-space: pre-wrap;">{{ msg.content }}</span>
            </div>
          </div>

        </div>

        <!-- Input -->
        <div class="cb-footer">
          <div class="cb-input-wrap" :class="{ 'cb-input-wrap--focus': focused }">
            <input
              ref="inputRef"
              v-model="inputText"
              type="text"
              class="cb-input"
              placeholder="Posez votre question…"
              :disabled="loading"
              maxlength="500"
              @keydown.enter.prevent="sendMessage"
              @focus="focused = true"
              @blur="focused = false"
            />
            <button
              class="cb-send"
              :class="{ 'cb-send--active': inputText.trim() && !loading }"
              :disabled="loading || !inputText.trim()"
              @click="sendMessage"
            >
              <i v-if="!loading" class="bi bi-send-fill"></i>
              <span v-else class="cb-spinner"></span>
            </button>
          </div>
          <p class="cb-footer-hint">Données en temps réel · Ikhwan Food ERP</p>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ChatbotWidget",
  data() {
    return {
      open: false,
      messages: [],
      inputText: "",
      loading: false,
      focused: false,
      suggestions: [
        "Chiffre d'affaires ce mois ?",
        "Top 5 clients impayés ?",
        "Stock disponible ?",
        "Dépenses cette année ?",
      ],
    };
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => {
          if (this.$refs.inputRef) this.$refs.inputRef.focus();
        });
      }
    },

    clearChat() {
      this.messages = [];
    },

    useSuggestion(text) {
      this.inputText = text;
      this.$nextTick(() => {
        if (this.$refs.inputRef) this.$refs.inputRef.focus();
      });
    },

    async sendMessage() {
      const question = this.inputText.trim();
      if (!question || this.loading) return;

      this.inputText = "";
      this.messages.push({ role: "user", content: question });

      const botIdx = this.messages.length;
      this.messages.push({ role: "bot", content: "", loading: true });
      this.loading = true;
      this.scrollToBottom();

      try {
        const response = await this.$api.instance.post(
          "chatbot/ask",
          { question },
          { timeout: 180000 }
        );
        const data = response.data;
        this.$set(this.messages, botIdx, {
          role: "bot",
          content: data.answer || "Aucune réponse obtenue.",
          loading: false,
        });
      } catch (err) {
        const errMsg =
          (err.response && err.response.data && err.response.data.answer)
            ? err.response.data.answer
            : "Erreur de connexion. Veuillez réessayer.";
        this.$set(this.messages, botIdx, {
          role: "bot",
          content: errMsg,
          loading: false,
        });
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.msgContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
/* ── FAB ─────────────────────────────────────────────────────────── */
.cb-fab {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px 0 6px;
  height: 52px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
  transition: transform 0.2s, box-shadow 0.2s;
}
.cb-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(99, 102, 241, 0.55);
}
.cb-fab--open {
  background: #64748b;
  box-shadow: 0 4px 14px rgba(0,0,0,0.18);
  padding: 0 14px;
}
.cb-fab-inner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.cb-fab-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* ── Panel ───────────────────────────────────────────────────────── */
.cb-panel {
  position: fixed;
  bottom: 94px;
  right: 28px;
  width: 380px;
  height: 560px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  z-index: 9998;
  overflow: hidden;
  border: 1px solid rgba(99,102,241,0.12);
}

/* ── Transition ──────────────────────────────────────────────────── */
.cb-slide-enter-active, .cb-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.cb-slide-enter, .cb-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}

/* ── Header ──────────────────────────────────────────────────────── */
.cb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  flex-shrink: 0;
}
.cb-header-left { display: flex; align-items: center; gap: 10px; }
.cb-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
}
.cb-header-title { font-size: 14px; font-weight: 700; color: #fff; }
.cb-header-sub   { font-size: 11px; color: rgba(255,255,255,0.75); display: flex; align-items: center; gap: 4px; margin-top: 1px; }
.cb-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 2px rgba(74,222,128,0.3);
}
.cb-header-actions { display: flex; gap: 4px; }
.cb-icon-btn {
  width: 30px; height: 30px;
  border-radius: 8px;
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px;
  transition: background 0.15s;
}
.cb-icon-btn:hover { background: rgba(255,255,255,0.28); }

/* ── Messages ────────────────────────────────────────────────────── */
.cb-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8f9ff;
  scroll-behavior: smooth;
}
.cb-messages::-webkit-scrollbar { width: 4px; }
.cb-messages::-webkit-scrollbar-track { background: transparent; }
.cb-messages::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

/* Welcome */
.cb-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 8px;
  margin: auto 0;
}
.cb-welcome-icon {
  width: 56px; height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  display: flex; align-items: center; justify-content: center;
  font-size: 26px;
  color: #7c3aed;
  margin-bottom: 12px;
}
.cb-welcome-title { font-size: 15px; font-weight: 700; color: #1e1b4b; margin: 0 0 6px; }
.cb-welcome-sub   { font-size: 12px; color: #6b7280; margin: 0 0 16px; line-height: 1.5; }
.cb-suggestions   { display: flex; flex-direction: column; gap: 7px; width: 100%; }
.cb-suggestion {
  background: #fff;
  border: 1.5px solid #e0e7ff;
  color: #4f46e5;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.cb-suggestion:hover { background: #eef2ff; border-color: #a5b4fc; }

/* Message rows */
.cb-msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.cb-msg--user { flex-direction: row-reverse; }
.cb-msg--bot  { flex-direction: row; }

.cb-msg-avatar {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  font-size: 13px;
  color: #fff;
  flex-shrink: 0;
}

/* Bubbles */
.cb-bubble {
  max-width: 78%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
}
.cb-bubble--user {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.cb-bubble--bot {
  background: #fff;
  color: #1e293b;
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

/* Typing dots */
.cb-typing {
  display: flex; gap: 4px; align-items: center; height: 18px;
}
.cb-typing span {
  width: 7px; height: 7px; border-radius: 50%;
  background: #a5b4fc;
  animation: cb-bounce 1.3s infinite ease-in-out;
}
.cb-typing span:nth-child(2) { animation-delay: 0.18s; }
.cb-typing span:nth-child(3) { animation-delay: 0.36s; }
@keyframes cb-bounce {
  0%, 70%, 100% { transform: translateY(0); }
  35% { transform: translateY(-7px); }
}

/* ── Footer / Input ──────────────────────────────────────────────── */
.cb-footer {
  padding: 12px 14px 10px;
  border-top: 1px solid #f1f3f9;
  background: #fff;
  flex-shrink: 0;
}
.cb-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f4f5fb;
  border-radius: 12px;
  padding: 4px 4px 4px 14px;
  border: 1.5px solid transparent;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.cb-input-wrap--focus {
  border-color: #a5b4fc;
  box-shadow: 0 0 0 3px rgba(165,180,252,0.2);
  background: #fff;
}
.cb-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: #1e293b;
  padding: 6px 0;
}
.cb-input::placeholder { color: #9ca3af; }
.cb-input:disabled { opacity: 0.5; }

.cb-send {
  width: 34px; height: 34px;
  border-radius: 9px;
  border: none;
  background: #e0e7ff;
  color: #a5b4fc;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.18s, color 0.18s, transform 0.12s;
}
.cb-send--active {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  box-shadow: 0 3px 10px rgba(99,102,241,0.35);
}
.cb-send--active:hover { transform: scale(1.07); }
.cb-send:disabled { cursor: not-allowed; }

/* Spinner */
.cb-spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: cb-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes cb-spin { to { transform: rotate(360deg); } }

.cb-footer-hint {
  font-size: 10px;
  color: #cbd5e1;
  text-align: center;
  margin: 7px 0 0;
  letter-spacing: 0.2px;
}

/* ── Responsive ──────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .cb-panel { width: calc(100vw - 24px); right: 12px; bottom: 82px; height: 520px; }
  .cb-fab   { bottom: 20px; right: 14px; }
}
</style>
